import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  AtSign,
  Check,
  CircleCheckBig,
  CircleHelp,
  Euro,
  Info,
  type LucideIcon,
  MapPinned,
  MessageCircleMore,
  MousePointerClick,
  Pencil,
  ReceiptEuro,
  ReceiptText,
  RectangleEllipsis,
  Send,
  Signature,
  SquareArrowOutUpRight,
  TimerOff,
  Users,
  X,
} from "lucide-react";
import type React from "react";
import { type FormEvent, useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Can } from "@/components/can";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { PassengerRequestsSection } from "@/features/claims/components/passenger-requests/passenger-requests-section";
import { useCurrentUser } from "@/hooks/use-current-user";
import { cn } from "@/lib/utils";

import { useClaim, useClaimFeatureFlags } from "../../api/use-claim";
import {
  useOpenTrackingPreview,
  useSendTrackingLinkEmail,
  useUpdateAcquisitionEmail,
} from "../../api/use-claim-actions";
import {
  type FamilyClaimAcquisition,
  generatePostmarkUrl,
  getAcquisitionPassengerName,
  getAcquisitionStepBadge,
  getAcquisitionStepCircleClasses,
} from "./acquisition-steps";
import { LastSentEmailLine } from "./last-sent-email";
import { PassengerAnswersPanel } from "./passenger-answers-panel";
import { findFamilyUsingEmail } from "./shared-email";
import { SharedEmailConfirmDialog } from "./shared-email-confirm-dialog";

function StepIcon({ step }: { step: FamilyClaimAcquisition["step"] }) {
  const className = "h-6 w-6";
  if (step === "DECLINED" || step === "REJECTED_PRE_EXISTING_CLAIM") {
    return <X className={className} aria-hidden="true" />;
  }
  if (step === "EXPIRED") {
    return <TimerOff className={className} aria-hidden="true" />;
  }
  if (step === "CONFIRM") {
    return <MessageCircleMore className={className} aria-hidden="true" />;
  }
  if (step === "EXPENSES") {
    return <Euro className={className} aria-hidden="true" />;
  }
  if (step === "SIGNATURE") {
    return <Signature className={className} aria-hidden="true" />;
  }
  if (step === "COMPLETE") {
    return <CircleCheckBig className={className} aria-hidden="true" />;
  }
  return <RectangleEllipsis className={className} aria-hidden="true" />;
}

/** Ouvre la page de suivi telle que la voit le passager — aucun email n'est envoyé. */
function OpenTrackingPreviewButton({ acquisitionId }: { acquisitionId: string }) {
  const openPreview = useOpenTrackingPreview(acquisitionId);

  return (
    <Button variant="outline" size="sm" onClick={() => openPreview.mutate()} disabled={openPreview.isPending}>
      <SquareArrowOutUpRight />
      Ouvrir la page de suivi
    </Button>
  );
}

/**
 * Le bouton reste visible quand la page de suivi n'est pas activée pour
 * l'agence : désactivé et accompagné d'une infobulle, il fait découvrir la
 * fonctionnalité plutôt que de la cacher.
 */
function SendTrackingLinkButton({
  claimId,
  acquisitionId,
  toEmail,
  enabled,
  alreadySent,
}: {
  claimId: string;
  acquisitionId: string;
  toEmail: string;
  enabled: boolean;
  alreadySent: boolean;
}) {
  const sendTrackingLink = useSendTrackingLinkEmail(claimId, acquisitionId, toEmail);

  const button = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => sendTrackingLink.mutate()}
      disabled={!enabled || sendTrackingLink.isPending}
    >
      <Send />
      {alreadySent ? "Renvoyer le lien de suivi" : "Envoyer le lien de suivi"}
    </Button>
  );

  if (enabled) {
    return button;
  }

  // Un bouton désactivé n'émet pas d'événement de survol : l'infobulle doit
  // s'accrocher à un wrapper, pas au bouton lui-même.
  return (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex" />}>{button}</TooltipTrigger>
      <TooltipContent className="max-w-xs">
        La page de suivi passager n'est pas encore activée pour cette agence. Contactez-nous pour l'activer.
      </TooltipContent>
    </Tooltip>
  );
}

const euroFormatter = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });

function formatDateTime(date: Date | string): string {
  return format(new Date(date), "d MMM yyyy 'à' HH:mm", { locale: fr });
}

/** Ouvertures par le passager — les prévisualisations admin n'y sont pas comptées. Rien n'est affiché tant qu'il n'a pas ouvert. */
function formatOpenCount(count: number): string {
  return count === 1 ? "ouvert 1 fois" : `ouvert ${count} fois`;
}

/** Libellé de ligne : gras et foncé, icône grise — le style des lignes d'origine, dans la grille. */
function DetailLabel({ icon: Icon, children }: { icon: LucideIcon; children: React.ReactNode }) {
  return (
    <dt className="flex min-h-7 items-center gap-2 whitespace-nowrap font-semibold text-gray-900">
      <Icon aria-hidden="true" className="h-5 w-5 text-gray-500" />
      {children}
    </dt>
  );
}

function Note({ title, children }: { title: string; children: string }) {
  return (
    <div>
      <p className="text-sm font-medium text-gray-900">{title}</p>
      <blockquote className="mt-1.5 whitespace-pre-line border-l-2 border-gray-200 pl-3 text-sm text-gray-700">
        « {children} »
      </blockquote>
    </div>
  );
}

/** Ce que change « Modifier » : la plupart des admins ne savent pas que la séquence repart. */
function EmailEditHelp() {
  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            className="ml-1 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="À quoi sert la modification de l'email ?"
          />
        }
      >
        <CircleHelp className="size-3.5" aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent className="max-w-xs space-y-2 p-3 text-xs leading-relaxed">
        <p className="font-medium text-gray-900">Corriger l'adresse de la famille</p>
        <p className="text-gray-600">
          À utiliser après un email rejeté (bounce) ou une faute de frappe. Tous les emails de l'acquisition —
          relances, signature, reçu — partent ensuite vers la nouvelle adresse.
        </p>
        <p className="text-gray-600">
          La séquence de relances de l'étape en cours redémarre à la nouvelle adresse. Le lien déjà reçu par
          le passager reste valable.
        </p>
      </PopoverContent>
    </Popover>
  );
}

interface FamilyAcquisitionCardProps {
  acquisition: FamilyClaimAcquisition;
  claimId: string;
}

/** Port of a single family acquisition block of AcquisitionHistory.vue. */
export function FamilyAcquisitionCard({ acquisition, claimId }: FamilyAcquisitionCardProps) {
  const { can } = useCurrentUser();
  const isUpclaimAdmin = can(UPCLAIM_ROLES);
  const badge = getAcquisitionStepBadge(acquisition.step);
  const family = acquisition.family;
  const { data: featureFlags } = useClaimFeatureFlags(claimId);
  const trackingEnabled = (featureFlags ?? []).includes("PASSENGER_CLAIM_TRACKING");

  // `null` = lecture. Le brouillon porte l'état d'édition : pas de booléen à tenir synchronisé.
  const [emailDraft, setEmailDraft] = useState<string | null>(null);
  const updateEmail = useUpdateAcquisitionEmail(claimId, acquisition.id);
  const isEditingEmail = emailDraft !== null;
  const { data: claim } = useClaim(claimId);
  // Family already reached at the draft address — the correction waits for the jurist to confirm.
  const [sharingFamilyName, setSharingFamilyName] = useState<string | null>(null);

  function saveEmail() {
    if (emailDraft) {
      updateEmail.mutate({ email: emailDraft.trim() }, { onSuccess: () => setEmailDraft(null) });
    }
  }

  function submitEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const sharingFamily =
      emailDraft && claim
        ? findFamilyUsingEmail(claim.usersClaimsFamilies, emailDraft, acquisition.familyId)
        : undefined;
    if (sharingFamily) {
      setSharingFamilyName(sharingFamily.familyName ?? "");
      return;
    }
    saveEmail();
  }

  const passengerNames = family.usersClaims.map(({ user }) => getAcquisitionPassengerName(user));

  return (
    <div className="my-8 text-left">
      <div className="relative px-4">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex items-center justify-start">
          <span className="inline-flex items-center bg-slate-50 px-3 text-base font-semibold leading-6 text-gray-900">
            <div>
              <span
                className={cn(
                  "flex h-12 w-12 items-center justify-center rounded-full border-4 bg-white shadow ring-2 ring-white",
                  getAcquisitionStepCircleClasses(acquisition.step),
                )}
              >
                <StepIcon step={acquisition.step} />
              </span>
            </div>
            {family.usersClaims.length === 1 ? (
              <span className="ml-4">{passengerNames[0]}</span>
            ) : (
              <span className="ml-4">Famille {family.familyName}</span>
            )}
          </span>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-right italic">Envoyé le {new Date(acquisition.createdAt).toLocaleString()}</div>
        <div className="rounded-lg border bg-white">
          <div className="space-y-6 p-6">
            <div>
              <div className="flex items-center text-sm">
                {badge ? (
                  <span className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                    <svg className={cn("h-1.5 w-1.5", badge.dotClass)} viewBox="0 0 6 6" aria-hidden="true">
                      <circle cx="3" cy="3" r="3" />
                    </svg>
                    {badge.label}
                  </span>
                ) : null}
                {acquisition.stepUpdatedAt ? (
                  <>
                    <span className="mx-2 text-sm">·</span>
                    <span className="text-sm italic">
                      Depuis le {format(new Date(acquisition.stepUpdatedAt), "dd MMM yyyy", { locale: fr })}
                    </span>
                  </>
                ) : null}
              </div>
            </div>

            <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-6 gap-y-2 text-sm">
              <DetailLabel icon={Users}>{passengerNames.length > 1 ? "Passagers" : "Passager"}</DetailLabel>
              <dd className="flex min-h-7 items-center text-gray-900">{passengerNames.join(", ")}</dd>

              <DetailLabel icon={AtSign}>Email</DetailLabel>
              <dd className="min-w-0">
                {isEditingEmail ? (
                  // Valider / annuler à côté du champ : la commande reste là où se fait la saisie.
                  <form className="flex items-center gap-1.5" onSubmit={submitEmail}>
                    <Input
                      aria-label="Email de la famille"
                      autoFocus
                      className="w-72"
                      onChange={(event) => setEmailDraft(event.target.value)}
                      required
                      size="sm"
                      type="email"
                      value={emailDraft}
                    />
                    <Button
                      aria-label="Enregistrer l'email"
                      className="text-green-600 hover:bg-green-50 hover:text-green-700"
                      disabled={updateEmail.isPending || !emailDraft?.trim()}
                      size="icon-sm"
                      type="submit"
                      variant="outline"
                    >
                      <Check />
                    </Button>
                    <Button
                      aria-label="Annuler"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      disabled={updateEmail.isPending}
                      onClick={() => setEmailDraft(null)}
                      size="icon-sm"
                      type="button"
                      variant="outline"
                    >
                      <X />
                    </Button>
                  </form>
                ) : (
                  <div className="flex min-h-7 items-center gap-1">
                    <Can
                      permissions={UPCLAIM_ROLES}
                      fallback={
                        <a className="link truncate" href={`mailto:${family.email}`}>
                          {family.email}
                        </a>
                      }
                    >
                      <a
                        className="link truncate"
                        href={generatePostmarkUrl(family.email ?? "")}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {family.email}
                      </a>
                    </Can>
                    {/* Correction réservée à Upclaim — l'API refuse aussi les autres rôles. */}
                    {isUpclaimAdmin ? (
                      <Button
                        aria-label="Modifier l'email"
                        className="text-gray-500"
                        onClick={() => setEmailDraft(family.email ?? acquisition.toEmail)}
                        size="icon-sm"
                        variant="ghost"
                      >
                        <Pencil />
                      </Button>
                    ) : null}
                    {isUpclaimAdmin ? <EmailEditHelp /> : null}
                  </div>
                )}
                {/* `lastSentEmail` is `.allow(null)` on the API, but the SDK generator drops the null
                  from an object schema — hence the guard inside the component. */}
                <LastSentEmailLine email={acquisition.lastSentEmail} />
              </dd>

              {acquisition.signature ? (
                <>
                  <DetailLabel icon={ReceiptEuro}>Montant total</DetailLabel>
                  <dd className="flex min-h-7 items-center text-gray-900 tabular-nums">
                    {euroFormatter.format(acquisition.signature.totalAmountEuro / 100)}
                  </dd>
                </>
              ) : null}

              {acquisition.step === "SIGNATURE" || acquisition.step === "COMPLETE" ? (
                <Can permissions={UPCLAIM_ROLES}>
                  <DetailLabel icon={Signature}>Signature</DetailLabel>
                  <dd className="flex min-h-7 items-center">
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={`https://yousign.app/auth/workspace/requests/${acquisition.signature?.thirdId}`}
                      className="link inline-flex items-center gap-1"
                    >
                      Ouvrir dans YouSign
                      <SquareArrowOutUpRight aria-hidden="true" className="size-3.5" />
                    </a>
                  </dd>
                </Can>
              ) : null}

              {/* Le lien des emails du parcours (confirmation, dépenses, signature) — pas la page de suivi. */}
              <DetailLabel icon={MousePointerClick}>Lien du parcours</DetailLabel>
              <dd className="flex min-h-7 items-center">
                {acquisition.linkClickedAt ? (
                  <span className="text-gray-900">Cliqué le {formatDateTime(acquisition.linkClickedAt)}</span>
                ) : (
                  <span className="text-gray-500">Pas encore cliqué</span>
                )}
              </dd>

              {acquisition.mayHaveEnterpriseExpenses ? (
                <>
                  <DetailLabel icon={ReceiptText}>Dépenses</DetailLabel>
                  <dd className="flex min-h-7 items-center text-gray-900">
                    Il a pu y avoir des dépenses pour son entreprise
                  </dd>
                </>
              ) : null}
            </dl>

            {acquisition.preExistingClaim ? (
              <div className="flex gap-3 rounded-md bg-blue-50 p-4">
                <Info className="h-5 w-5 shrink-0 text-blue-400" aria-hidden="true" />
                <p className="text-sm text-blue-700">
                  Le passager avait déjà engagé une demande d'indemnisation.
                </p>
              </div>
            ) : null}

            {acquisition.passengerNote ? (
              <Note title="Note passager">{acquisition.passengerNote}</Note>
            ) : null}
            {acquisition.passengerAnswers ? (
              <PassengerAnswersPanel value={acquisition.passengerAnswers} />
            ) : null}
            {acquisition.expensesNote ? (
              <Note title="Note sur les dépenses">{acquisition.expensesNote}</Note>
            ) : null}
          </div>

          {/* Pièces demandées au passager : réservé à Upclaim, dans son propre bloc, avant la page de suivi. */}
          {isUpclaimAdmin ? (
            <div className="border-t px-6 py-4">
              <PassengerRequestsSection className="mt-0" claimId={claimId} acquisition={acquisition} />
            </div>
          ) : null}

          {/* La page de suivi : l'état de son lien et les deux actions qui la concernent, ensemble. */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4">
            <p className="flex items-center gap-2 text-sm">
              <MapPinned aria-hidden="true" className="size-4 text-gray-500" />
              <span className="font-semibold text-gray-900">Page de suivi</span>
              <span className="text-gray-400" aria-hidden="true">
                ·
              </span>
              {acquisition.trackingLinkSentAt ? (
                <span className="text-gray-600">
                  Lien envoyé le {formatDateTime(acquisition.trackingLinkSentAt)}
                  {acquisition.trackingLinkOpenedCount > 0
                    ? ` · ${formatOpenCount(acquisition.trackingLinkOpenedCount)}`
                    : null}
                </span>
              ) : (
                <span className="text-gray-500">Lien pas encore envoyé</span>
              )}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {trackingEnabled ? <OpenTrackingPreviewButton acquisitionId={acquisition.id} /> : null}
              {/* Renvoyer reste possible : le passager a perdu l'email, ou ne l'a jamais ouvert. */}
              <SendTrackingLinkButton
                claimId={claimId}
                acquisitionId={acquisition.id}
                toEmail={acquisition.toEmail}
                enabled={trackingEnabled}
                alreadySent={Boolean(acquisition.trackingLinkSentAt)}
              />
            </div>
          </div>
        </div>
      </div>

      <SharedEmailConfirmDialog
        familyName={sharingFamilyName}
        onCancel={() => setSharingFamilyName(null)}
        onConfirm={() => {
          setSharingFamilyName(null);
          saveEmail();
        }}
      />
    </div>
  );
}
